import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { StatsBand } from "@/components/StatsBand";
import { FAQSection } from "@/components/FAQSection";
import { iconFor } from "@/lib/iconMap";
import DATA from "@/data/servicePagesData.json";
import { Check } from "lucide-react";

const Reveal = ({ children, className = "", delay = 0 }) => {
  const ref = useRef(null);
  const [on, setOn] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const reduced = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      setOn(true);
      return undefined;
    }
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && (setOn(true), obs.disconnect())),
      { threshold: 0.15 }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className={`${className} transition-all duration-700 ease-out ${on ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

export default function ServiceDetailPage({ skey }) {
  const S = DATA.services[skey];
  const bullets = DATA.machineBullets;
  if (!S) return null;
  return (
    <div data-testid={`service-page-${skey}`} className="bg-white">
      <SiteHeader />
      <main>
        {/* Hero */}
        <section data-testid="service-hero" className="relative overflow-hidden bg-[#0b0b0c] hero-noise">
          <img src={S.images.hero} alt="" className="absolute inset-0 h-full w-full object-cover" />
          <div
            className="absolute inset-0"
            style={{ background: "linear-gradient(90deg, rgba(2,2,2,0.86) 0%, rgba(2,2,2,0.64) 50%, rgba(2,2,2,0.86) 100%)" }}
          />
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 min-h-[480px] flex items-center justify-center pt-32 pb-16 md:pt-36 md:pb-20">
            <div className="text-center max-w-3xl mx-auto">
              <p className="font-nunito text-[11px] sm:text-xs font-extrabold uppercase tracking-[0.3em] text-[#ff6b63] mb-5">
                Services · {S.cat}
              </p>
              <h1
                data-testid="service-hero-title"
                className="font-nunito font-extrabold text-white text-3xl sm:text-4xl lg:text-[3rem] leading-[1.1] tracking-tight"
              >
                {S.title}
              </h1>
              <p className="mt-5 text-[#dedede] text-sm sm:text-base md:text-lg leading-relaxed max-w-2xl mx-auto">{S.sub}</p>
              <div className="mt-8">
                <Link
                  to="/#visibility-audit"
                  data-testid="service-hero-cta"
                  className="inline-flex justify-center items-center rounded-[10px] bg-[#ee2e24] px-7 py-3.5 font-nunito text-[13px] font-extrabold uppercase tracking-[0.12em] text-white shadow-[0_14px_40px_rgba(238,46,36,0.3)] hover:bg-[#d92820] active:scale-[0.98] transition-colors duration-150"
                >
                  Get My Free Visibility Audit
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Intro + side photo */}
        <section data-testid="service-intro" className="bg-white py-16 md:py-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="font-nunito text-2xl sm:text-3xl font-extrabold text-[#383838] heading-redul">{S.introH}</h2>
                <p className="mt-5 text-[15px] text-[#6e6e6e] leading-relaxed">{S.p1}</p>
              </div>
              <Reveal>
                <img
                  src={S.images.side}
                  alt={S.title}
                  className="w-full rounded-[14px] shadow-[0_18px_50px_rgba(2,2,2,0.18)]"
                  data-testid="service-side-photo"
                />
              </Reveal>
            </div>
            <p className="mt-8 text-[15px] text-[#6e6e6e] leading-relaxed max-w-4xl">{S.p2}</p>
          </div>
        </section>

        {/* Includes */}
        <section data-testid="service-includes" className="bg-[#fafafa] border-y border-[#f1f1f1] py-16 md:py-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="text-center max-w-2xl mx-auto">
              <p className="font-nunito text-[11px] font-extrabold uppercase tracking-[0.3em] text-[#ee2e24]">What's included</p>
              <h2 className="mt-3 font-nunito text-2xl sm:text-3xl font-extrabold text-[#383838] heading-redul-center">
                Everything this engine ships with.
              </h2>
            </div>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {S.includes.map((item, i) => (
                <Reveal key={item.title} delay={i * 70}>
                  <div
                    data-testid={`service-include-card-${i + 1}`}
                    className="h-full rounded-[12px] border border-[#ececec] bg-white p-6 shadow-[0_6px_18px_rgba(2,2,2,0.04)] hover:-translate-y-1 hover:shadow-[0_14px_40px_rgba(2,2,2,0.1)] hover:border-[#ee2e24]/30 transition-transform duration-200"
                  >
                    <div className="h-10 w-10 rounded-full bg-[#ee2e24]/[0.08] flex items-center justify-center">
                      <Check size={18} strokeWidth={2} className="text-[#ee2e24]" />
                    </div>
                    <h3 className="mt-4 font-nunito text-[15px] font-extrabold text-[#383838]">{item.title}</h3>
                    <p className="mt-2 text-[13px] text-[#6e6e6e] leading-relaxed">{item.line}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* Part of the machine */}
        <section data-testid="service-machine" className="bg-[#0b0b0c] py-16 md:py-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <p className="font-nunito text-[11px] font-extrabold uppercase tracking-[0.3em] text-[#ff6b63]">Part of the machine</p>
              <h2 className="mt-3 font-nunito text-2xl sm:text-3xl font-extrabold text-white">No service runs alone.</h2>
              <p className="mt-5 text-[15px] text-[#dedede] leading-relaxed">
                {S.title} plugs into The Web Marketing Machine™ — so every dollar compounds across channels.
              </p>
            </div>
            <div className="space-y-4">
              {bullets.map((b, i) => {
                const Icon = iconFor(b.icon);
                return (
                  <Reveal key={b.icon} delay={i * 90}>
                    <div data-testid={`service-machine-bullet-${i + 1}`} className="flex items-center gap-4 rounded-[14px] border border-white/10 bg-white/[0.05] p-5">
                      <span className="h-10 w-10 shrink-0 rounded-full border border-white/20 bg-white/[0.06] flex items-center justify-center">
                        <Icon size={17} strokeWidth={1.75} className="text-[#ee2e24]" />
                      </span>
                      <p className="text-[14.5px] text-[#ececec]">{b.text}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        <StatsBand />

        <FAQSection heading={`${S.title} — common questions`} items={S.faq.map((f) => ({ q: f.q, a: f.a }))} injectSchema />

        {/* CTA */}
        <section data-testid="service-cta" className="bg-[#fafafa] border-t border-[#f1f1f1] py-16 md:py-20">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="font-nunito text-2xl sm:text-3xl font-extrabold text-[#383838]">Ready to add this engine?</h2>
            <p className="mt-4 text-[15px] text-[#6e6e6e] leading-relaxed">
              Start with the Free Visibility Audit — we'll show you exactly what {S.title} would move first, from a US-based strategist, in 2 business days or less.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
              <Link
                to="/#visibility-audit"
                data-testid="service-cta-primary-button"
                className="w-full sm:w-auto inline-flex justify-center items-center rounded-[10px] bg-[#ee2e24] px-7 py-3.5 font-nunito text-[13px] font-extrabold uppercase tracking-[0.12em] text-white shadow-[0_14px_40px_rgba(238,46,36,0.3)] hover:bg-[#d92820] active:scale-[0.98] transition-colors duration-150"
              >
                Get My Free Visibility Audit
              </Link>
              <Link
                to="/contact"
                data-testid="service-cta-secondary-button"
                className="w-full sm:w-auto inline-flex justify-center items-center rounded-[10px] border border-[#d9d9d9] px-7 py-3.5 font-nunito text-[13px] font-extrabold uppercase tracking-[0.12em] text-[#383838] hover:border-[#ee2e24] hover:text-[#ee2e24] transition-colors duration-150"
              >
                Talk to a Strategist
              </Link>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
